/*
 * @Author: chenchaoqiang@leedarson.com
 * @Date: 2022-08-03 09:16:55
 * Copyright 2022 Leedarson, All Rights Reserved.
 */
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosPromise,
  AxiosDefaults,
} from 'axios';
import i18n from 'i18next';
import {
  TOKEN,
  ACCESS_TOKEN_RESPONSE_PARAM,
  REFRESH_TOKEN,
  HOUSE_ID,
} from '@constant';
import defaultOptions from './options';
import noop from '../noop';
import { isInApp } from '../terminal';
import Modal from '@root/components/modal';
import native from '@root/native';
import { Report } from '@root/logger';

const REFRESH_TOKEN_REQUEST_METHOD = 'POST';
const REFRESH_TOKEN_REQUEST_URL = '/apiLogin/users/refreshToken';

let isRefreshing = false; // 正在换取令牌标识
let failedQueue: Array<any> = []; // 换取令牌过程中，有别的请求进来，就添加到此队列中
let newData: any = noop.EMPTY_OBJECT; // 新令牌数据

interface IParameters {
  expiresIn: number;
  expiresTimestamp: number;
  [propName: string]: any;
}

const processQueue = (error: any, accessToken = null) => {
  // 参数来自于 `failedQueue.push({ resolve, reject })`
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error); // 注意: reject 只支持一个参数
    } else {
      resolve(accessToken);
    }
  });
  failedQueue = [];
};

class Http {
  private instance: AxiosInstance;
  private parameters: IParameters;
  // public options: AxiosRequestConfig;
  constructor() {
    // this.options = Object.assign(defaultOptions, config); // Object
    this.instance = axios.create(defaultOptions);
    this.parameters = {
      expiresIn: 0,
      expiresTimestamp: 0,
    };
    this.interceptors(this.instance);
  }

  public setOptions(options: AxiosRequestConfig) {
    const { defaults } = this.instance;
    const { baseURL, headers = {} } = options || noop.EMPTY_OBJECT;
    if (Object.prototype.toString.call(baseURL) === '[object String]') {
      defaults.baseURL = baseURL;
    }
    if (Object.prototype.toString.call(headers) === '[object Object]') {
      Object.keys(headers).forEach((key) => {
        const value = headers[key];
        if (value) {
          // TODO types
          (defaults.headers as any)[key] = value; // 扩展处理方式, 包括 appId, terminal, token 等
        }
      });
    }
  }

  public setParameters(parameters: IParameters) {
    if (Object.prototype.toString.call(parameters) === '[object Object]') {
      this.parameters = { ...this.parameters, ...parameters };
    }
  }

  public request(options: AxiosRequestConfig): AxiosPromise {
    options = this.mergeConfig(options);

    return this.instance(options);
  }

  // 请求拦截
  private interceptors(instance: AxiosInstance) {
    instance.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        const _baseHeader = {
          terminal: isInApp ? 'app' : 'web',
          appcode: 'aidot',
          tenantcode: 'm071',
          locale: localStorage.getItem('locale') || 'en-US',
        };
        config.headers = config.headers
          ? Object.assign(config.headers, _baseHeader)
          : _baseHeader;

        return config;
      },
      (reason) => {
        return Promise.reject(reason);
      },
    );

    instance.interceptors.response.use(
      (resp: any) => {
        const { data } = resp || noop.EMPTY_OBJECT;
        if (data) {
          if (data?.code === undefined) {
            return data;
          }
          if (data?.code === 200) {
            return data?.data;
          }
          return Promise.reject(data);
        }
        return data;
      },
      (reason: any) => {
        const { defaults } = instance;
        // const { axios, lifecycles, constants, parameters } = self;
        const { config: originalRequest, response, code, message } = reason;

        const path = originalRequest.url;
        const requestData = {
          data: originalRequest?.data || {},
          response: response?.data || {},
        };

        if (!response || !response.data) {
          Report.upload({
            module: 'HTTP',
            level: 'error',
            event: '12004',
            message: 'Network error, please try again later.',
            path,
            request: requestData,
          });

          return Promise.reject({
            code: 12004,
            desc: 'Network error, please try again later.',
            data: reason,
          });
        }
        if (
          response.data.code === 21025 ||
          response.data.code === 21027 ||
          response.data.code === 70032
        ) {
          // 退出登录
          localStorage.removeItem(TOKEN);
          localStorage.removeItem(REFRESH_TOKEN);
          localStorage.removeItem(HOUSE_ID);
          if (isInApp) {
            Modal.alert({
              title: i18n.t('session_expired'),
              content: i18n.t('session_expired_tips'),
              confirm: i18n.t('ok'),
              onConfirm: () => {
                if (window.WebViewJavascriptBridge) {
                  native.webView.close();
                }
              },
            });
          } else {
            window.location.href = `${
              process.env.REACT_APP_ROUTE_BASE_NAME
            }/login?redirectUrl=${encodeURIComponent(
              `${window.location.pathname}${window.location.search}`,
            )}`;
          }
          return Promise.reject(reason);
        }

        // 置换token
        if (response.data.code === 21026) {
          const diff = this.parameters.expiresTimestamp - Date.now();
          const safeTime = diff < this.parameters.expiresIn * 1000 && diff > 0;
          const oldAccessToken = originalRequest.headers[TOKEN];
          const newAccessToken = newData[ACCESS_TOKEN_RESPONSE_PARAM];
          if (safeTime && newAccessToken && oldAccessToken !== newAccessToken) {
            originalRequest.headers[TOKEN] = newAccessToken;
            return instance(originalRequest);
          }

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject }); // 此处并未resolve或者reject，所以then不会执行
            })
              .then((accessToken) => {
                // then 参数来自于 `processQueue(null, accessToken)` --> `resolve(accessToken)`
                originalRequest.headers[TOKEN] = accessToken;
                return instance(originalRequest); // 处理换取accessToken过程中进来的请求
              })
              .catch((err) => {
                const desc = '队列补发失败';
                return Promise.reject({ code: 12138, desc, data: err }); // 12138 Unknown Locator
              });
          }
          isRefreshing = true;
          return new Promise((resolve, reject) => {
            const obj = {
              [REFRESH_TOKEN]: this.parameters.refreshToken,
            }; // 请求参数
            let promise = null;
            if (/get/i.test(REFRESH_TOKEN_REQUEST_METHOD)) {
              promise = instance.get(REFRESH_TOKEN_REQUEST_URL, {
                params: obj,
              });
            } else if (/post/i.test(REFRESH_TOKEN_REQUEST_METHOD)) {
              promise = instance.post(REFRESH_TOKEN_REQUEST_URL, obj);
            } else {
              throw new Error('method overflow');
            }
            promise
              .then((res: any) => {
                isRefreshing = false;
                newData = res;
                const accessToken = res[ACCESS_TOKEN_RESPONSE_PARAM];
                localStorage.setItem(TOKEN, accessToken);
                (defaults.headers as any)[TOKEN] = accessToken; // 设置后续的请求头
                originalRequest.headers[TOKEN] = accessToken; // 设置最早的请求头
                processQueue(null, accessToken); // 处理换取accessToken过程中进来的请求
                resolve(instance(originalRequest)); // 处理最早的请求
              })
              .catch((err: any) => {
                isRefreshing = false;
                newData = noop.EMPTY_OBJECT;
                const desc = '换取令牌失败';
                processQueue(err, null); // 此语句是`promise.catch`
                reject({ code: 403, desc, data: err });

                Report.upload({
                  module: 'HTTP',
                  level: 'error',
                  event: 'refreshToken',
                  message: err,
                  path,
                  request: requestData,
                });
              })
              .finally(() => {
                isRefreshing = false;
              });
          });
        }

        Report.upload({
          module: 'HTTP',
          level: 'error',
          event: code,
          message: message,
          path,
          request: requestData,
        });

        return Promise.reject(reason);
      },
    );
  }
  private mergeConfig(options: AxiosRequestConfig): AxiosRequestConfig {
    return options;
  }
}

export default Http;
